import type { AxiosInstance } from 'axios';

// Core functions: perform raw HTTP only (keep logic in callers)
const getAnnualExpensesCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  year: number,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(
    `/bp_get_annual_expenses_report_information?bp_year=${encodeURIComponent(
      String(year),
    )}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getMonthlyExpensesCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
  startISO?: string,
  endISO?: string,
) => {
  const query =
    startISO && endISO
      ? `?bp_start_date=${encodeURIComponent(startISO)}&bp_end_date=${encodeURIComponent(
          endISO,
        )}`
      : '';
  return axiosinstance.get(`/bp_get_expenses_report_information${query}`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

const getCategoryExpensesCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
  startISO?: string,
  endISO?: string,
) => {
  const query =
    startISO && endISO
      ? `?bp_start_date=${encodeURIComponent(startISO)}&bp_end_date=${encodeURIComponent(
          endISO,
        )}`
      : '';
  return axiosinstance.get(
    `/bp_get_expense_type_expenses_report_information${query}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getExpenseTypesCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(`/bp_get_expense_types`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

export type CreateExpensePayload = {
  category: string;
  amount: number;
  description?: string;
  date: string; // ISO string
};

const createExpenseCore = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: CreateExpensePayload,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.post(`/bp_create_expense_information`, payload, {
    headers: {
      Authorization: 'Bearer ' + String(auth?.accessToken || ''),
      'Content-Type': 'application/json',
    },
  });
};

// ─── Expenses list (paginated) ────────────────────────────────────────────
const getExpensesListCore = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
  filters: ExpensesListFilters,
) => {
  const parts: string[] = [
    `bp_start_date=${encodeURIComponent(filters.startISO)}`,
    `bp_end_date=${encodeURIComponent(filters.endISO)}`,
  ];
  if (filters.expenseType && filters.expenseType !== 'all') {
    parts.push(`bp_expense_type=${encodeURIComponent(filters.expenseType)}`);
  }
  if (filters.page) parts.push(`bp_page=${filters.page}`);
  if (filters.limit) parts.push(`bp_limit=${filters.limit}`);
  if (filters.sort) parts.push(`bp_sort=${encodeURIComponent(filters.sort)}`);
  return axiosinstance.get(`/bp_get_expenses_list?${parts.join('&')}`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

// ─── Expense detail ──────────────────────────────────────────────────────
const getExpenseDetailCore = async (
  axiosinstance: AxiosInstance,
  expenseId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.get(`/bp_get_expense_detail/${encodeURIComponent(expenseId)}`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

// ─── Delete expense ──────────────────────────────────────────────────────
const deleteExpenseCore = async (
  axiosinstance: AxiosInstance,
  expenseId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosinstance.delete(
    `/bp_delete_expense_information/${encodeURIComponent(expenseId)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Public wrappers with basic error propagation
export const getAnnualExpenses = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  year: number,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getAnnualExpensesCore(axiosinstance, ownerId, year, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getMonthlyExpenses = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
  dateRange?: { startISO?: string; endISO?: string },
) => {
  try {
    return await getMonthlyExpensesCore(
      axiosinstance,
      ownerId,
      auth,
      dateRange?.startISO,
      dateRange?.endISO,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getCategoryExpenses = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
  dateRange?: { startISO?: string; endISO?: string },
) => {
  try {
    return await getCategoryExpensesCore(
      axiosinstance,
      ownerId,
      auth,
      dateRange?.startISO,
      dateRange?.endISO,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getExpenseTypes = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getExpenseTypesCore(axiosinstance, ownerId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createExpense = async (
  axiosinstance: AxiosInstance,
  ownerId: string,
  payload: CreateExpensePayload,
  auth: { accessToken?: string | null },
) => {
  try {
    return await createExpenseCore(axiosinstance, ownerId, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getExpensesList = async (
  axiosinstance: AxiosInstance,
  auth: { accessToken?: string | null },
  filters: ExpensesListFilters,
) => {
  try {
    return await getExpensesListCore(axiosinstance, auth, filters);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getExpenseDetail = async (
  axiosinstance: AxiosInstance,
  expenseId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getExpenseDetailCore(axiosinstance, expenseId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteExpense = async (
  axiosinstance: AxiosInstance,
  expenseId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await deleteExpenseCore(axiosinstance, expenseId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

// ─── Types ───────────────────────────────────────────────────────────────

export type AnnualExpensePoint = {
  month?: string | number;
  totalsByType?: Record<string, number>;
  total?: number;
};

export type MonthlyExpenses = {
  payments: number;
  stocks: number;
};

export type CategoryExpense = {
  name: string;
  amount: number;
};

export type ExpenseDocument = {
  _id: string;
  bp_expense_amount: number;
  bp_expense_type: string;
  bp_expense_description: string;
  bp_expense_date: string;
  bp_link_type?: string | null;
  bp_link_id?: string | null;
  bp_metadata?: Record<string, any> | null;
  bp_expense_created_at?: string;
  bp_expense_updated_at?: string;
};

export type ExpensePagination = {
  page: number;
  limit: number;
  totalDocs: number;
  totalPages: number;
  hasNextPage: boolean;
};

export type ExpensePreviousPeriod = {
  totalExpenses: number;
  changePercent: number;
  changeDirection: 'up' | 'down' | 'flat';
};

export type ExpenseSummary = {
  totalsByType: Record<string, number>;
  totalExpenses: number;
  payments: number;
  stocks: number;
  otherExpensesTotal: number;
  manualCount: number;
  autoRecordedCount: number;
  activeCategoriesCount: number;
  previousPeriod: ExpensePreviousPeriod;
};

export type ExpensesListFilters = {
  startISO: string;
  endISO: string;
  expenseType?: string;
  page?: number;
  limit?: number;
  sort?: string;
};
